import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Security|Ban/Watch|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateBanWatch';
export type SPCandidateBanWatchLocNames =
	| `${LocNameStart}TitleTableSection`
	| `${LocNameStart}TitlePageSection`
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}LabelDate`
	| `${LocNameStart}LabelUser`
	| `${LocNameStart}LabelComment`
	| `${LocNameStart}LabelWatchedUntil`
	| `${LocNameStart}LabelWatched`
	| `${LocNameStart}LabelBannedUntil`
	| `${LocNameStart}LabelBanned`
	| `${LocNameStart}ButtonRemoveBan`
	| `${LocNameStart}ButtonBan`
	| `${LocNameStart}ButtonStopWatch`
	| `${LocNameStart}ButtonWatch`
	| `${LocNameStart}NotificationBanWatchSuccess`
	| `${LocNameStart}NotificationBanWatchFailure`
	| `${LocNameStart}ButtonRemoveRestriction`
	| `${LocNameStart}ButtonRestrict`
	| `${LocNameStart}LabelTestingRestricted`
	| `${LocNameStart}ButtonUpdateBan`
	| `${LocNameStart}ButtonUpdateWatch`;

export const spCandidateBanWatchLocs: LocDefs<SPCandidateBanWatchLocNames> = {
	SPCandidateBanWatchTitlePageSection: {
		defaultValue: 'Ban/Watch',
		description: 'Page Section Title',
		displayPath: `${pathStart}Page Section Title`,
		displayType: 'Title',
		legacyName: 'Core/CandidateBan.Title.BanWatch',
		...sharedProperties,
	},
	SPCandidateBanWatchTitleTableSection: {
		defaultValue: 'History',
		description: 'Table Section Title',
		displayPath: `${pathStart}Table Section Title`,
		displayType: 'Title',
		legacyName: 'Core/CandidateBan.Title.BanWatchHistory',
		...sharedProperties,
	},
	SPCandidateBanWatchTitleTab: {
		defaultValue: 'Candidate Ban/Watch',
		description: 'Tab Title',
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'Core/CandidateBan.Title.Tab',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelDate: {
		defaultValue: 'Date',
		description: 'Column heading for: Date.',
		displayPath: `${pathStart}Date`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.ColumnHeading.Date',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelUser: {
		defaultValue: 'User',
		description: 'Column heading for: User.',
		displayPath: `${pathStart}User`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.ColumnHeading.User',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelComment: {
		defaultValue: 'Comment',
		description: 'Column heading for: Comment.',
		displayPath: `${pathStart}Comment`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.ColumnHeading.Comment',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelWatchedUntil: {
		defaultValue: 'Watched Until',
		description: 'Field label for: Watched Until.',
		displayPath: `${pathStart}Watched Until`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.FieldLabel.WatchedUntil',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelWatched: {
		defaultValue: 'Watch',
		description: 'Field label for: Watch.',
		displayPath: `${pathStart}Watch`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.FieldLabel.Watched',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelBannedUntil: {
		defaultValue: 'Banned Until',
		description: 'Field label for: Banned Until.',
		displayPath: `${pathStart}Banned Until`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.FieldLabel.BannedUntil',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelBanned: {
		defaultValue: 'Banned',
		description: 'Field label for: Banned.',
		displayPath: `${pathStart}Banned`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.FieldLabel.Banned',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonRemoveBan: {
		defaultValue: 'Remove Ban',
		description: 'Button text for: Remove Ban.',
		displayPath: `${pathStart}Remove Ban`,
		displayType: 'Button',
		legacyName: 'Core/CandidateBan.Button.RemoveBan',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonUpdateBan: {
		defaultValue: 'Update Ban',
		description: 'Button text for: Update Ban.',
		displayPath: `${pathStart}Update Ban`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonUpdateWatch: {
		defaultValue: 'Update Watch',
		description: 'Button text for: Update Watch.',
		displayPath: `${pathStart}Update Watch`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonBan: {
		defaultValue: 'Ban',
		description: 'Button text for: Ban.',
		displayPath: `${pathStart}Ban`,
		displayType: 'Button',
		legacyName: 'Core/CandidateBan.Button.Ban',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonStopWatch: {
		defaultValue: 'Stop Watch',
		description: 'Button text for: Stop Watch.',
		displayPath: `${pathStart}Stop Watch`,
		displayType: 'Button',
		legacyName: 'Core/CandidateBan.Button.StopWatch',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonWatch: {
		defaultValue: 'Watch',
		description: 'Button text for: Watch.',
		displayPath: `${pathStart}Watch`,
		displayType: 'Button',
		legacyName: 'Core/CandidateBan.Button.StopWatch',
		...sharedProperties,
	},
	SPCandidateBanWatchNotificationBanWatchSuccess: {
		defaultValue: 'Candidate status successfully updated',
		description: 'Notification text for: Candidate status successfully updated.',
		displayPath: `${pathStart}Candidate status successfully updated`,
		displayType: 'Notification',
		legacyName: 'Core/CandidateBan.NotificationMessage.Success',
		...sharedProperties,
	},
	SPCandidateBanWatchNotificationBanWatchFailure: {
		defaultValue: 'Something went wrong, please contact support',
		description: 'Notification text for: Something went wrong, please contact support.',
		displayPath: `${pathStart}Something went wrong, please contact support`,
		displayType: 'Notification',
		legacyName: 'Core/CandidateBan.NotificationMessage.Error',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonRemoveRestriction: {
		defaultValue: 'Remove Restriction',
		description: 'Button text for: Remove Restriction.',
		displayPath: `${pathStart}Remove Restriction Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCandidateBanWatchButtonRestrict: {
		defaultValue: 'Restrict',
		description: 'Button text for: Restrict Testing.',
		displayPath: `${pathStart}Restrict Testing Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCandidateBanWatchLabelTestingRestricted: {
		defaultValue: 'Testing Restricted',
		description: 'Label for testing restricted.',
		displayPath: `${pathStart}Testing Restricted Label`,
		displayType: 'Label',
		legacyName: 'Core/CandidateBan.ColumnHeading.Comment',
		...sharedProperties,
	},
};

export type SPCandidateBanWatchLocType = LocMap<SPCandidateBanWatchLocNames>;
